/**
 * PaymentsReport — o que entrou, o que falta entrar, o que está vencido e por
 * qual forma de pagamento. Lê o módulo financeiro (VIEW
 * `finance_entries_with_status`, via `useFinanceEntriesRaw`).
 *
 * Dois portões, os dois obrigatórios:
 * - **Papel** (`canSeeFinance`): a RLS de `finance_entries` é owner/admin-only,
 *   então um `member` receberia ZERO linhas e veria um relatório vazio sem
 *   explicação — parecendo bug. Ele vê "Acesso restrito".
 * - **Plano** (`reports_advanced`): usuário grátis vê o padrão da página —
 *   conteúdo borrado com amostra + CTA, nunca tela vazia.
 *
 * A base de data (pagamento × vencimento) é escolha EXPLÍCITA do usuário e fica
 * rotulada na tela; agregação toda em `@/lib/paymentsReport` (puro/testável).
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  CreditCard,
  Crown,
  DollarSign,
  Lock,
  Scale,
  TrendingDown,
  Users,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { formatBRL, formatBRLShort } from '@/lib/financeLabels';
import { MONEY_AXIS_WIDTH, moneyAxisTick } from '@/components/reports/axisFormat';
import {
  AwaitingData,
  BlurOverlay,
  ListTail,
  WarningCard,
} from '@/components/reports/ReportGates';
import {
  listLimitFor,
  ReportViewToggle,
  type ReportViewMode,
} from '@/components/reports/ReportViewToggle';
import {
  aggregateByPaymentMethod,
  aggregateByFinanceCategory,
  aggregateMonthly,
  overdueEntries,
  paymentsSummary,
  topClientsByReceived,
  DEFAULT_PAYMENTS_REPORT_SORT,
  type IsoDateRange,
  type PaymentDateBasis,
  type PaymentsReportSort,
} from '@/lib/paymentsReport';
import type { FinanceEntryWithStatus } from '@/types/database';

// ===== amostra para o usuário grátis (será borrada) =====

const SAMPLE_METHODS = [
  { key: 'pix', label: 'PIX', total: 5200, percent: 52, color: '#10b981' },
  { key: 'card_credit', label: 'Cartão de crédito', total: 2400, percent: 24, color: '#6366f1' },
  { key: 'cash', label: 'Dinheiro', total: 1500, percent: 15, color: '#22c55e' },
  { key: '__unspecified__', label: 'Não informado', total: 900, percent: 9, color: '#94a3b8' },
];

const SAMPLE_MONTHLY = [
  { month: '1', label: 'jan', received: 4200, paid: 2100 },
  { month: '2', label: 'fev', received: 6100, paid: 2600 },
  { month: '3', label: 'mar', received: 5400, paid: 3000 },
  { month: '4', label: 'abr', received: 7300, paid: 2400 },
];

interface PaymentsReportProps {
  entries: FinanceEntryWithStatus[];
  clientNames: Map<string, string>;
  categoryNames?: Map<string, string>;
  sort?: PaymentsReportSort;
  loading?: boolean;
  truncated?: boolean;
  loadFailed?: boolean;
  /** Intervalo do DateRangeFilter, já convertido para 'YYYY-MM-DD' local. */
  range: IsoDateRange;
  basis: PaymentDateBasis;
  onBasisChange: (basis: PaymentDateBasis) => void;
  /** `reports_advanced.allowed`. */
  allowed: boolean;
  onUnlock: () => void;
  /** `useRole().canSeeFinance` — owner/admin da empresa ativa. */
  canSeeFinance: boolean;
  viewMode: ReportViewMode;
  onViewModeChange: (mode: ReportViewMode) => void;
}

/** Quantos clientes o RESUMO mostra. O Detalhado mostra o ranking inteiro. */
const CLIENTS_SUMMARY_LIMIT = 5;

export function PaymentsReport({
  entries,
  clientNames,
  categoryNames = new Map(),
  sort = DEFAULT_PAYMENTS_REPORT_SORT,
  loading = false,
  truncated = false,
  loadFailed = false,
  range,
  basis,
  onBasisChange,
  allowed,
  onUnlock,
  canSeeFinance,
  viewMode,
  onViewModeChange,
}: PaymentsReportProps) {
  const navigate = useNavigate();

  // Entradas da EMPRESA ATIVA. A queryKey inclui o company_id, então trocar de
  // empresa refaz a leitura em vez de reaproveitar os dados da anterior.

  // Nomes de cliente para o ranking e a lista de vencidos. Mesma queryKey do
  // FinanceEntryModal, então o cache é compartilhado; offline-first pelo cache.

  const summary = useMemo(
    () => paymentsSummary(entries, range, basis),
    [entries, range, basis],
  );
  const methodSlices = useMemo(
    () => aggregateByPaymentMethod(entries, range, basis),
    [entries, range, basis],
  );
  const categorySlices = useMemo(
    () => aggregateByFinanceCategory(entries, categoryNames, range, basis, sort),
    [entries, categoryNames, range, basis, sort],
  );
  const monthly = useMemo(
    () => aggregateMonthly(entries, range, basis),
    [entries, range, basis],
  );
  // Ranking COMPLETO; o corte é da tela e depende do modo (Resumo × Detalhado).
  // Antes o limite ficava escondido no default do agregador (5) e o usuário não
  // tinha como ver o resto sem exportar.
  const allClients = useMemo(
    () => topClientsByReceived(entries, clientNames, range, basis, Number.MAX_SAFE_INTEGER),
    [entries, clientNames, range, basis],
  );
  const clientsLimit = listLimitFor(viewMode, CLIENTS_SUMMARY_LIMIT);
  const topClients = useMemo(
    () => allClients.slice(0, clientsLimit),
    [allClients, clientsLimit],
  );
  const overdue = useMemo(
    () => overdueEntries(entries, range, clientNames),
    [entries, range, clientNames],
  );

  const hasMonthlyData = monthly.some((m) => m.received > 0 || m.paid > 0);


  // Portão de PAPEL antes do de plano: um member não vê nem a amostra, porque
  // para ele o problema não é o plano — é permissão.
  if (!canSeeFinance) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Lock className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            O relatório de pagamentos usa dados financeiros e está disponível apenas para o
            proprietário e administradores desta empresa.
          </p>
          <Button variant="outline" className="mt-5" onClick={() => navigate('/dashboard')}>
            Voltar ao início
          </Button>
        </CardContent>
      </Card>
    );
  }

  const Gate = ({ children }: { children: React.ReactNode }) => (
    <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
      {children}
    </BlurOverlay>
  );

  const basisNote =
    basis === 'paid'
      ? 'Recebido, pago e saldo contam pela data do pagamento.'
      : 'Recebido, pago e saldo contam pela data de vencimento.';

  return (
    <div className="space-y-6">
      {allowed && (
        <ReportViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          hint={`${allClients.length} ${allClients.length === 1 ? 'cliente' : 'clientes'} com recebimento`}
        />
      )}
      {allowed && truncated && (
        <WarningCard>
          Há mais lançamentos do que o limite de segurança. Os números estão
          incompletos; reduza o período ou aplique filtros.
        </WarningCard>
      )}
      {allowed && loadFailed && (
        <WarningCard>
          Não foi possível atualizar os lançamentos. Os dados exibidos podem vir
          do cache offline.
        </WarningCard>
      )}

      {/* Base de data — escolha explícita, nunca implícita */}
      <Card className="bg-muted/30">
        <CardContent className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Select value={basis} onValueChange={(v) => onBasisChange(v as PaymentDateBasis)}>
              <SelectTrigger className="h-9 flex-1 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Por data de pagamento (realizado)</SelectItem>
                <SelectItem value="due">Por data de vencimento (previsto)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {basisNote} <span className="font-medium">A receber e Vencido</span> contam sempre pela
            data de vencimento dentro do período — um título em aberto não tem data de pagamento.
          </p>
          <p className="text-[11px] text-muted-foreground">
            {entries.length}{' '}
            {entries.length === 1 ? 'lançamento no recorte' : 'lançamentos no recorte'}
          </p>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          icon={<Wallet className="h-3.5 w-3.5 text-emerald-600" />}
          tint="bg-emerald-100 dark:bg-emerald-950/30"
          label="Recebido"
          value={allowed ? formatBRLShort(summary.received) : 'R$ 10,0K'}
          valueClass="text-emerald-600"
          hint={allowed
            ? `${summary.receivedCount} ${summary.receivedCount === 1 ? 'recebimento' : 'recebimentos'}`
            : '18 recebimentos'}
          allowed={allowed}
          onUnlock={onUnlock}
        />
        {/* "no período" no hint de propósito: estes dois contam títulos com
            VENCIMENTO dentro do intervalo, não a carteira aberta inteira. Sem
            isso um R$ 0 pareceria "não tenho nada a receber". */}
        <KpiCard
          icon={<DollarSign className="h-3.5 w-3.5 text-blue-600" />}
          tint="bg-blue-100 dark:bg-blue-950/30"
          label="A receber"
          value={allowed ? formatBRLShort(summary.toReceive) : 'R$ 3,2K'}
          valueClass="text-blue-600"
          hint={allowed ? `${summary.toReceiveCount} vencendo no período` : '5 no período'}
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
          tint="bg-red-100 dark:bg-red-950/30"
          label="Vencido"
          value={allowed ? formatBRLShort(summary.overdue) : 'R$ 850'}
          valueClass="text-red-600"
          hint={allowed ? `${summary.overdueCount} em atraso no período` : '2 em atraso'}
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<TrendingDown className="h-3.5 w-3.5 text-orange-600" />}
          tint="bg-orange-100 dark:bg-orange-950/30"
          label="Pago (saídas)"
          value={allowed ? formatBRLShort(summary.expensesPaid) : 'R$ 4,1K'}
          valueClass="text-orange-600"
          hint={allowed
            ? `${summary.expensesPaidCount} ${summary.expensesPaidCount === 1 ? 'pagamento' : 'pagamentos'}`
            : '9 pagamentos'}
          allowed={allowed}
          onUnlock={onUnlock}
        />
      </div>

      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <Gate>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Saldo do período</p>
                  <p className="text-[11px] text-muted-foreground/70">Recebido − pago</p>
                </div>
              </div>
              <p
                className={
                  (allowed ? summary.balance : 5900) >= 0
                    ? 'text-2xl font-bold text-emerald-600'
                    : 'text-2xl font-bold text-red-600'
                }
              >
                {allowed ? formatBRL(summary.balance) : 'R$ 5.900,00'}
              </p>
            </div>
          </Gate>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            Receitas e despesas por categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && categorySlices.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 border-b pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Categoria</span>
                  <span>Receitas</span>
                  <span>Despesas</span>
                </div>
                {categorySlices
                  .slice(0, listLimitFor(viewMode, 6))
                  .map((row) => (
                    <div
                      key={row.categoryId ?? '__none__'}
                      className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 border-b py-2 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{row.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {row.count} {row.count === 1 ? 'lançamento' : 'lançamentos'}
                          {' · '}saldo {formatBRL(row.balance)}
                        </p>
                      </div>
                      <span className="text-xs tabular-nums text-emerald-600">
                        + {formatBRLShort(row.income)}
                      </span>
                      <span className="text-xs tabular-nums text-red-600">
                        − {formatBRLShort(row.expense)}
                      </span>
                    </div>
                  ))}
                <ListTail
                  shown={Math.min(categorySlices.length, listLimitFor(viewMode, 6))}
                  total={categorySlices.length}
                  noun="categoria"
                />
              </div>
            ) : allowed ? (
              <AwaitingData
                hint={loading ? 'Carregando categorias' : 'Nenhum lançamento categorizado no período'}
              />
            ) : (
              <div className="space-y-2">
                {['Serviços', 'Materiais', 'Sem categoria'].map((name, index) => (
                  <div key={name} className="flex justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{name}</span>
                    <span className="text-xs">R$ {index + 1}.200 · R$ {index + 1}50</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* Recebimentos por forma de pagamento */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" />
            Recebimentos por forma de pagamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {(() => {
              const slices = allowed ? methodSlices : SAMPLE_METHODS;
              if (allowed && slices.length === 0) {
                return <AwaitingData hint="Registre recebimentos para ver a distribuição" />;
              }
              // O recharts ESPALHA os campos do dado nas props do label, então um
              // campo nosso chamado `percent` (0–100) sobrescreveria o `percent`
              // fracionário dele e o rótulo sairia "5556%". Tiramos o campo do
              // dado do gráfico e deixamos o recharts calcular; a tabela abaixo
              // continua usando `slices[].percent`.
              const chartData = slices.map(({ percent: _drop, ...rest }) => rest);
              return (
                <div className="space-y-4">
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={75}
                          paddingAngle={4}
                          dataKey="total"
                          nameKey="label"
                          label={({ percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {slices.map((s) => (
                            <Cell key={s.key} fill={s.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number, name: string) => [formatBRL(value), name]}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => <span className="text-xs">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Tabela: valor e % por método */}
                  <div className="space-y-1">
                    {slices.map((s) => (
                      <div
                        key={s.key}
                        className="flex items-center justify-between border-b py-2 last:border-0"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: s.color }}
                          />
                          <span className="truncate text-sm">{s.label}</span>
                        </div>
                        <div className="shrink-0 text-right">
                          <span className="text-sm font-semibold">{formatBRLShort(s.total)}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {s.percent.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Gate>
        </CardContent>
      </Card>

      {/* Evolução mensal — recebido × pago */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            Evolução mensal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            <div className="h-64">
              {allowed && !hasMonthlyData ? (
                <AwaitingData hint="Registre pagamentos para ver a evolução" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={allowed ? monthly : SAMPLE_MONTHLY}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={moneyAxisTick}
                      width={MONEY_AXIS_WIDTH}
                      className="text-muted-foreground"
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [formatBRL(value), name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={30}
                      formatter={(value) => <span className="text-xs">{value}</span>}
                    />
                    <Bar dataKey="received" name="Recebido" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="paid" name="Pago" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Gate>
        </CardContent>
      </Card>

      {/* Top clientes por valor recebido */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Top clientes por valor recebido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && topClients.length > 0 ? (
              <div className="space-y-2">
                {topClients.map((c, index) => (
                  <div
                    key={c.clientId ?? `sem-cliente-${index}`}
                    className="flex items-center justify-between border-b py-2 last:border-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-4 text-xs text-muted-foreground">{index + 1}.</span>
                      <span className="truncate text-sm font-medium">{c.name}</span>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-sm font-semibold text-emerald-600">
                        {formatBRLShort(c.total)}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">{c.count}x</span>
                    </div>
                  </div>
                ))}
                <ListTail
                  total={allClients.length}
                  shown={topClients.length}
                  noun="cliente"
                />
              </div>
            ) : allowed ? (
              <AwaitingData hint="Vincule recebimentos a clientes para ver o ranking" />
            ) : (
              <div className="space-y-2">
                {['Cliente A', 'Cliente B', 'Cliente C'].map((name, i) => (
                  <div key={name} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{name}</span>
                    <span className="text-sm">R$ {(4200 - i * 900).toLocaleString('pt-BR')}</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* Vencidos — o item mais acionável */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Recebimentos vencidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && overdue.length > 0 ? (
              <div className="space-y-2">
                {overdue.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{row.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.clientName ? `${row.clientName} · ` : ''}
                        venceu em {format(new Date(`${row.dueDate}T00:00:00`), "dd 'de' MMM", { locale: ptBR })}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-red-600">{formatBRL(row.amount)}</p>
                      <p className="text-xs text-red-600/80">
                        {row.daysLate} {row.daysLate === 1 ? 'dia' : 'dias'} de atraso
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : allowed ? (
              <div className="py-6 text-center">
                <p className="text-sm font-medium text-emerald-600">Nenhum recebimento vencido</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {loading ? 'Carregando…' : 'Está tudo em dia no período selecionado.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  { d: 'Serviço A', v: 500, l: 12 },
                  { d: 'Serviço B', v: 350, l: 4 },
                ].map((s) => (
                  <div key={s.d} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{s.d}</span>
                    <span className="text-sm">
                      R$ {s.v},00 · {s.l} dias
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {!allowed && (
        <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
          <CardContent className="flex flex-col items-center py-6 text-center">
            <h3 className="text-sm font-semibold">Relatório de pagamentos</h3>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              Veja quanto entrou, o que falta receber, o que está vencido e por qual forma de
              pagamento. Disponível no plano PRO.
            </p>
            <Button3D color="amber" size="sm" className="mt-4" onClick={onUnlock}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== KPI =====

interface KpiCardProps {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
  valueClass: string;
  hint: string;
  allowed: boolean;
  onUnlock: () => void;
}

/**
 * Recebe `allowed`/`onUnlock` em vez de um componente-wrapper: um componente
 * definido no corpo do render tem identidade nova a cada render e faria o React
 * remontar a subárvore inteira toda vez.
 */
function KpiCard({
  icon,
  tint,
  label,
  value,
  valueClass,
  hint,
  allowed,
  onUnlock,
}: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-3">
        <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
          <div className="flex flex-col">
            <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full ${tint}`}>
              {icon}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`truncate text-base font-bold ${valueClass}`}>{value}</p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground/70">{hint}</p>
          </div>
        </BlurOverlay>
      </CardContent>
    </Card>
  );
}
